// NeuroMotor AI — device-only store (localStorage). Non-diagnostic.
const KEY = "silentscan_neuromotor_v1";

const DEFAULT = {
  profile: {
    hand_mode: "both", // "left" | "right" | "both"
    accessibility: { large_targets: false, high_contrast: false, reduced_motion: false, voice_instructions: false },
    consent_camera: false,
  },
  level: 1,
  sessions: [], // { at, kind, level, accuracy, completion_ms, reaction_ms, errors, correct, total, assisted }
  personal_bests: {},
};

export const loadMotor = () => {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT };
    const p = JSON.parse(raw);
    return {
      ...DEFAULT,
      ...p,
      profile: { ...DEFAULT.profile, ...(p.profile || {}), accessibility: { ...DEFAULT.profile.accessibility, ...((p.profile || {}).accessibility || {}) } },
      personal_bests: { ...(p.personal_bests || {}) },
    };
  } catch { return { ...DEFAULT }; }
};

export const saveMotor = (s) => { try { localStorage.setItem(KEY, JSON.stringify(s)); } catch (e) { console.warn("motor save:", e); } };

export const recordMotorSession = (session) => {
  const s = loadMotor();
  const entry = {
    at: new Date().toISOString(),
    kind: session.kind || "target-reach",
    level: Number(session.level || s.level || 1),
    accuracy: Number(session.accuracy || 0),
    completion_ms: Number(session.completion_ms || 0),
    reaction_ms: Number(session.reaction_ms || 0),
    errors: Number(session.errors || 0),
    correct: Number(session.correct || 0),
    total: Number(session.total || 0),
    assisted: !!session.assisted,
  };
  s.sessions = [...(s.sessions || []).slice(-999), entry];

  // Personal bests
  const pb = { ...(s.personal_bests || {}) };
  const key = entry.kind;
  const prev = pb[key] || {};
  pb[key] = {
    best_accuracy: Math.max(prev.best_accuracy || 0, entry.accuracy),
    fastest_ms: prev.fastest_ms ? Math.min(prev.fastest_ms, entry.completion_ms || Infinity) : entry.completion_ms || 0,
    fastest_reaction: prev.fastest_reaction ? Math.min(prev.fastest_reaction, entry.reaction_ms || Infinity) : entry.reaction_ms || 0,
    longest_sequence: Math.max(prev.longest_sequence || 0, entry.correct || 0),
  };
  s.personal_bests = pb;

  // Adaptive level — recent 8 sessions
  const recent = s.sessions.slice(-8);
  const avgAcc = recent.reduce((a, x) => a + (x.accuracy || 0), 0) / Math.max(1, recent.length);
  if (avgAcc >= 85) s.level = Math.min(5, (s.level || 1) + 1);
  else if (avgAcc <= 50 && recent.length >= 4) s.level = Math.max(1, (s.level || 1) - 1);

  saveMotor(s);
  return s;
};

export const setHandMode = (mode) => {
  const s = loadMotor();
  s.profile = { ...s.profile, hand_mode: mode };
  saveMotor(s);
  return s;
};

export const aggregateMotor = (state) => {
  const s = state || loadMotor();
  const sessions = s.sessions || [];
  const total = sessions.length;
  if (!total) return { sessions: 0, tasks: 0, accuracy: 0, avg_time_ms: 0, avg_reaction_ms: 0, consistency: 0, level: s.level || 1, streak: 0 };
  const tasks = sessions.reduce((a, x) => a + (x.total || 0), 0);
  const accSum = sessions.reduce((a, x) => a + (x.accuracy || 0), 0);
  const timeList = sessions.filter((x) => x.completion_ms > 0).map((x) => x.completion_ms);
  const reactList = sessions.filter((x) => x.reaction_ms > 0).map((x) => x.reaction_ms);
  const days = new Set(sessions.map((x) => (x.at || "").slice(0, 10)));
  let streak = 0;
  const cursor = new Date();
  while (streak < 60) {
    const k = cursor.toISOString().slice(0, 10);
    if (days.has(k)) { streak += 1; cursor.setDate(cursor.getDate() - 1); } else break;
  }
  const consistency = Math.round((days.size / Math.max(1, 14)) * 100);
  return {
    sessions: total,
    tasks,
    accuracy: Math.round(accSum / total),
    avg_time_ms: timeList.length ? Math.round(timeList.reduce((a, x) => a + x, 0) / timeList.length) : 0,
    avg_reaction_ms: reactList.length ? Math.round(reactList.reduce((a, x) => a + x, 0) / reactList.length) : 0,
    consistency: Math.min(100, consistency),
    level: s.level || 1,
    streak,
  };
};

export const buildDemoMotor = () => {
  const now = Date.now();
  const sessions = [];
  for (let d = 20; d >= 0; d -= 1) {
    const learn = (20 - d) / 20;
    sessions.push({
      at: new Date(now - d * 86400000).toISOString(),
      kind: ["target-reach", "reaction", "sequence", "coordination"][d % 4],
      level: 1 + Math.floor(learn * 3),
      accuracy: Math.round(55 + learn * 35),
      completion_ms: Math.round(52000 - learn * 20000),
      reaction_ms: Math.round(1400 - learn * 400),
      errors: Math.max(0, 4 - Math.round(learn * 3)),
      correct: 8 + Math.round(learn * 4),
      total: 12,
      assisted: d > 12,
    });
  }
  return { ...DEFAULT, level: 3, sessions, personal_bests: { "target-reach": { best_accuracy: 92, fastest_ms: 28000, fastest_reaction: 890, longest_sequence: 12 } } };
};

export const motorTimeline = (state, days = 14) => {
  const s = state || loadMotor();
  const now = new Date();
  const out = [];
  for (let i = days - 1; i >= 0; i -= 1) {
    const d = new Date(now); d.setDate(now.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    const list = (s.sessions || []).filter((x) => (x.at || "").slice(0, 10) === key);
    const acc = list.length ? Math.round(list.reduce((a, x) => a + (x.accuracy || 0), 0) / list.length) : 0;
    const rt = list.filter((x) => x.reaction_ms > 0);
    const reaction = rt.length ? Math.round(rt.reduce((a, x) => a + x.reaction_ms, 0) / rt.length) : 0;
    const tt = list.filter((x) => x.completion_ms > 0);
    const timeSec = tt.length ? Math.round(tt.reduce((a, x) => a + x.completion_ms, 0) / tt.length / 1000) : 0;
    out.push({ day: key.slice(5), accuracy: acc, reaction, time: timeSec, sessions: list.length });
  }
  return out;
};

export const motorPlan = (state) => {
  const s = state || loadMotor();
  const lvl = s.level || 1;
  return [
    { kind: "target-reach", label: "Target Reach", level: lvl, blurb: "Reach toward a target and return to rest." },
    { kind: "reaction", label: "Reaction", level: Math.min(5, lvl + 1), blurb: "Raise a hand quickly when ready." },
    { kind: "sequence", label: "Sequence", level: lvl, blurb: "Touch nose → shoulder → knee in order." },
    { kind: "coordination", label: "Coordination", level: Math.max(1, lvl - 1), blurb: "Bring finger tips together and apart smoothly." },
  ];
};
